import React, { useState } from 'react';
import { User, Search, UserPlus, X, Users } from 'lucide-react';
import { Menu, Button, Avatar, Text, Flex, Box, TextInput, Divider, Badge, ActionIcon } from '@mantine/core';
import { useUsers } from '../hooks/useTodos';
import { useUser } from '../contexts/UserContext';
import { useNavigate } from 'react-router-dom';

interface ListSharingSelectorProps {
  sharedWith: string[];
  onChange: (userEmail: string, action: 'add' | 'remove') => void;
  placeholder?: string;
  compact?: boolean;
  onClose?: () => void;
}

export const ListSharingSelector: React.FC<ListSharingSelectorProps> = ({ 
  sharedWith = [], 
  onChange, 
  placeholder = "Share with users",
  compact = false,
  onClose
}) => {
  const [searchTerm, setSearchTerm] = useState('');
  const navigate = useNavigate();

  const { data: users = [], isLoading } = useUsers();
  const { currentUser } = useUser();
  
  // Filter out current user and already shared users, then apply search
  const availableUsers = users
    .filter(user => 
      user.email !== currentUser?.email && 
      !sharedWith.includes(user.email)
    )
    .filter(user =>
      user.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      user.email.toLowerCase().includes(searchTerm.toLowerCase())
    );

  const handleAddUser = (userEmail: string) => {
    onChange(userEmail, 'add');
    setSearchTerm('');
  };

  const handleRemoveUser = (userEmail: string) => {
    onChange(userEmail, 'remove');
  };

  const getDisplayText = () => {
    if (sharedWith.length === 0) {
      return placeholder;
    }
    if (sharedWith.length === 1) {
      const user = users.find(u => u.email === sharedWith[0]);
      return `Shared with ${user?.name || sharedWith[0]}`;
    }
    return `Shared with ${sharedWith.length} users`;
  };

  if (isLoading) {
    return (
      <Button 
        variant="default"
        size={compact ? 'compact-sm' : 'sm'}
        disabled
        leftSection={<Users size={16} />}
      >
        Loading users...
      </Button>
    );
  }

  return (
    <Menu position="bottom-start" withinPortal shadow="md">
      <Menu.Target>
        <Button
          variant="default"
          size={compact ? 'compact-sm' : 'sm'}
          leftSection={<Users size={16} />}
          rightSection={sharedWith.length > 0 ? <Badge size="sm" color="blue" variant="filled">{sharedWith.length}</Badge> : undefined}
          styles={{
            root: {
              border: '1px solid var(--mantine-color-gray-3)',
              '&:hover': {
                backgroundColor: 'var(--mantine-color-gray-0)',
                borderColor: 'var(--mantine-color-blue-4)',
              }
            }
          }}
        >
          {getDisplayText()}
        </Button>
      </Menu.Target>

      <Menu.Dropdown style={{ minWidth: '300px' }}>
        {/* Search input for large user lists */}
        {users.length >= 5 && (
          <Box p="sm">
            <TextInput
              placeholder="Search users..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.currentTarget.value)}
              leftSection={<Search size={14} />}
              size="sm"
              styles={{
                input: {
                  fontSize: '14px'
                }
              }}
            />
          </Box>
        )}

        {/* Currently shared users */}
        {sharedWith.length > 0 && (
          <>
            <Menu.Label>Currently shared with:</Menu.Label>
            {sharedWith.map(userEmail => {
              const user = users.find(u => u.email === userEmail);
              return (
                <Box key={userEmail} px="sm" py="xs">
                  <Flex align="center" justify="space-between">
                    <Flex align="center" gap="xs">
                      <Avatar 
                        size="sm" 
                        color="blue" 
                        radius="xl"
                        styles={{
                          root: { minWidth: '32px' }
                        }}
                      >
                        {user?.name ? user.name.split(' ').map(n => n[0]).join('').toUpperCase() : <User size={14} />}
                      </Avatar>
                      <Box style={{ minWidth: 0, flex: 1 }}>
                        <Text size="sm" fw={500} truncate>
                          {user?.name || userEmail.split('@')[0]}
                        </Text>
                        <Text size="xs" c="dimmed" truncate>
                          {userEmail}
                        </Text>
                      </Box>
                    </Flex>
                    <ActionIcon
                      size="sm"
                      variant="subtle"
                      color="red"
                      onClick={() => handleRemoveUser(userEmail)}
                      aria-label={`Remove ${user?.name || userEmail}`}
                    >
                      <X size={12} />
                    </ActionIcon>
                  </Flex>
                </Box>
              );
            })}
            <Divider my="xs" />
          </>
        )}

        {/* Available users to add */}
        {availableUsers.length > 0 && (
          <>
            {sharedWith.length > 0 && <Menu.Label>Add users:</Menu.Label>}
            {availableUsers.map(user => (
              <Menu.Item
                key={user.email}
                onClick={() => handleAddUser(user.email)}
                styles={{
                  item: {
                    padding: '8px 12px',
                  }
                }}
              >
                <Flex align="center" gap="xs">
                  <Avatar 
                    size="sm" 
                    color="green" 
                    radius="xl"
                    styles={{
                      root: { minWidth: '32px' }
                    }}
                  >
                    {user.name.split(' ').map(n => n[0]).join('').toUpperCase()}
                  </Avatar>
                  <Box style={{ minWidth: 0, flex: 1 }}>
                    <Text size="sm" fw={500} truncate>
                      {user.name}
                    </Text>
                    <Text size="xs" c="dimmed" truncate>
                      {user.email}
                    </Text>
                  </Box>
                </Flex>
              </Menu.Item>
            ))}
          </>
        )}

        {/* No results message */}
        {searchTerm && availableUsers.length === 0 && (
          <Box p="md" ta="center">
            <Text size="sm" c="dimmed">
              No users found matching "{searchTerm}"
            </Text>
          </Box>
        )}

        {/* Invite user option */}
        {!searchTerm && (
          <>
            <Divider my="xs" />
            <Menu.Item
              onClick={() => {
                onClose?.(); // Close the edit dialog first
                // Small delay to ensure dialog closes before navigation
                setTimeout(() => {
                  navigate('/settings', { state: { section: 'contacts' } });
                }, 100);
              }}
              color="blue"
              styles={{
                item: {
                  padding: '8px 12px',
                }
              }}
            >
              <Flex align="center" gap="xs">
                <Avatar size="sm" color="blue" radius="xl">
                  <UserPlus size={14} />
                </Avatar>
                <Box>
                  <Text size="sm" fw={500} c="blue">
                    Invite user
                  </Text>
                  <Text size="xs" c="dimmed">
                    Will discard any changes to the list
                  </Text>
                </Box>
              </Flex>
            </Menu.Item>
          </>
        )}
      </Menu.Dropdown>
    </Menu>
  );
};
